import BASE_URL from '../../utils/storage.js';
import {
  getBrowserNotificationFallbacks,
  getBrowserNotificationTitle,
} from '../../i18n/communications.js';
import { authService } from '../auth/authService.js';
import { getActiveLanguage } from '../../i18n/languageStore.js';
import { parseRetryAfter, websocketAPI } from '../api/websocketAPI.js';

export const notificationService = {
  LOCATION_UPDATE_TYPE: 'driver_location_update',
  userConnection: null, // Single WebSocket connection for user notifications
  connectionPromise: null,
  isConnecting: false,
  reconnectAttempts: 0,
  reconnectDelay: 1000,
  maxReconnectDelay: 30000,
  retryBaseDelay: null,
  reconnectTimer: null,
  shouldReconnect: true,
  generation: 0,
  sessionToken: null,
  connectionAbortController: null,
  connectionCancel: null,
  connectionTimeoutMs: 15000,
  userId: null, // Store current user ID
  lastSoundTime: 0,

  /**
   * Get current user ID for personal notification channel
   */
  async getCurrentUserId({ signal, generation = this.generation } = {}) {
    if (this.userId) return this.userId;

    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Get current user data to extract user ID
      const response = await fetch(`${BASE_URL}auth/users/me/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`
        },
        signal,
      });

      if (response.ok) {
        const userData = await response.json();
        if (generation !== this.generation || signal?.aborted) {
          throw this.createAbortError();
        }
        this.userId = userData.id;
        return this.userId;
      } else {
        throw new Error('Failed to fetch user data');
      }
    } catch (error) {
      console.error('Failed to resolve notification connection identity');
      throw error;
    }
  },

  /**
   * Connect to user's personal notification channel
   */
  connectToUserChannel() {
    const token = authService.getToken();
    if (!token) {
      return Promise.reject(new Error('Authentication required for WebSocket connection'));
    }

    if (this.sessionToken && this.sessionToken !== token) {
      this.disconnect();
    }
    this.sessionToken = token;
    this.shouldReconnect = true;

    if (this.userConnection?.readyState === WebSocket.OPEN) {
      return Promise.resolve(this.userConnection);
    }

    // The same promise covers ticket creation and the whole CONNECTING phase.
    if (this.connectionPromise) return this.connectionPromise;

    const generation = this.generation;
    const connectionPromise = this.createConnection(generation, token);
    this.connectionPromise = connectionPromise;

    connectionPromise.finally(() => {
      if (this.connectionPromise === connectionPromise) {
        this.connectionPromise = null;
      }
    }).catch(() => {});

    return connectionPromise;
  },

  async createConnection(generation, token) {
    this.isConnecting = true;
    const controller = new AbortController();
    let cancelConnection = null;
    this.connectionAbortController = controller;

    try {
      if (!this.userId) {
        await this.getCurrentUserId({ signal: controller.signal, generation });
      }

      const { url } = await websocketAPI.createTicket({ signal: controller.signal });
      if (!this.isCurrentGeneration(generation, token) || controller.signal.aborted) {
        throw this.createAbortError();
      }

      // Keep the URL exactly as returned by the backend.
      const ws = new WebSocket(url);
      this.userConnection = ws;

      return await new Promise((resolve, reject) => {
        let opened = false;
        let settled = false;

        const rejectOnce = (error) => {
          if (settled) return;
          settled = true;
          reject(error);
        };

        const connectionTimeout = setTimeout(() => {
          if (!this.isCurrentSocket(ws, generation)) return;
          rejectOnce(new Error('WebSocket connection timeout'));
          ws.close();
        }, this.connectionTimeoutMs);

        cancelConnection = () => {
          clearTimeout(connectionTimeout);
          rejectOnce(this.createAbortError());
        };
        this.connectionCancel = cancelConnection;

        ws.onopen = () => {
          if (!this.isCurrentSocket(ws, generation)) {
            ws.close(1000, 'Stale connection');
            return;
          }
          if (settled) {
            ws.close(1000, 'Connection attempt already finished');
            return;
          }

          opened = true;
          settled = true;
          clearTimeout(connectionTimeout);
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.retryBaseDelay = null;
          this.cancelReconnect();
          this.dispatchNotificationEvent('connection_established', {
            userId: this.userId,
            connected: true
          });
          resolve(ws);
        };

        ws.onmessage = (event) => {
          if (!this.isCurrentSocket(ws, generation)) return;
          try {
            this.handleNotification(JSON.parse(event.data));
          } catch {
            console.error('Failed to parse a notification WebSocket message');
          }
        };

        ws.onclose = (event) => {
          clearTimeout(connectionTimeout);
          if (!this.isCurrentSocket(ws, generation)) return;

          this.userConnection = null;
          this.isConnecting = false;
          this.dispatchNotificationEvent('connection_lost', {
            code: event.code,
          });

          if (event.code === 4401) {
            this.revokeAuthentication();
          } else if (opened && this.shouldReconnect && event.code !== 1000) {
            this.attemptReconnect(null, generation);
          }

          if (!opened) {
            const error = new Error(`WebSocket closed before opening (${event.code})`);
            error.closeCode = event.code;
            rejectOnce(error);
          }
        };

        ws.onerror = () => {
          if (!this.isCurrentSocket(ws, generation)) return;
          console.error('Notification WebSocket transport error');
          // Browsers normally follow this with onclose; the timeout is the fallback.
        };
      });
    } catch (error) {
      if (this.isCurrentGeneration(generation, token) && this.shouldRetryConnection(error)) {
        this.attemptReconnect(error, generation);
      }
      throw error;
    } finally {
      if (this.connectionCancel === cancelConnection) {
        this.connectionCancel = null;
      }
      if (this.connectionAbortController === controller) {
        this.connectionAbortController = null;
      }
      if (generation === this.generation && this.userConnection?.readyState !== WebSocket.CONNECTING) {
        this.isConnecting = false;
      }
    }
  },

  isCurrentGeneration(generation, token = this.sessionToken) {
    return generation === this.generation
      && this.shouldReconnect
      && this.sessionToken === token
      && authService.getToken() === token;
  },

  isCurrentSocket(ws, generation) {
    return generation === this.generation && this.userConnection === ws;
  },

  createAbortError() {
    const error = new Error('WebSocket connection attempt cancelled');
    error.name = 'AbortError';
    return error;
  },

  isAbortError(error) {
    return error?.name === 'AbortError';
  },

  shouldRetryConnection(error) {
    if (this.isAbortError(error)) return false;
    if (error?.status === 401 || error?.status === 403) return false;
    return ![1000, 1001, 4401].includes(error?.closeCode);
  },

  revokeAuthentication() {
    this.shouldReconnect = false;
    this.cancelReconnect();
    authService.removeToken();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('atrux:auth-revoked', {
        detail: {
          reason: 'websocket-session-revoked',
          message: 'Sesiunea a expirat sau a fost revocată.',
        },
      }));
    }
  },

  /**
   * Handle incoming notifications
   */
  isDriverLocationUpdate(notification) {
    const visited = new Set();

    const scan = (value, depth = 0) => {
      if (!value || typeof value !== 'object') {
        return false;
      }
      if (depth > 4 || visited.has(value)) {
        return false;
      }

      visited.add(value);

      const notificationType = value.notification_type || value.type;
      if (notificationType === this.LOCATION_UPDATE_TYPE) {
        return true;
      }

      const candidateKeys = ['message', 'notification', 'data', 'payload', 'event'];
      for (const key of candidateKeys) {
        if (scan(value[key], depth + 1)) {
          return true;
        }
      }

      return false;
    };

    return scan(notification);
  },

  handleNotification(data) {
    switch (data.type) {
      case 'connection_established':
        console.log('Connected to personal notifications channel');
        break;

      case 'auth_success':
        console.log('✅ Authentication successful for personal notifications');
        break;

      case 'auth_failed':
        console.error('❌ Authentication failed for personal notifications');
        this.disconnect();
        break;

      case 'notification':
        // Handle different notification message formats
        let notification = null;

        if (data.message && typeof data.message === 'object') {
          // Backend sends individual notification object in message field
          notification = data.message;
        } else if (data.notification) {
          // Alternative format with notification field
          notification = data.notification;
        } else if (data.id && data.title) {
          // Direct notification object
          notification = data;
        }

        if (notification) {
          const notificationFallbacks = getBrowserNotificationFallbacks();
          const notificationType = notification.notification_type || notification.type;
          const isDriverLocationUpdate = this.isDriverLocationUpdate(notification);

          // Skip browser notification + sound for live driver location updates
          if (!isDriverLocationUpdate) {
            this.showNotification(
              notification.title || getBrowserNotificationTitle(notificationType),
              notification.message || notificationFallbacks.newBody,
              { sourcePayload: notification }
            );
          }

          // Keep live tracking updates out of the notifications list/badge flow
          if (!isDriverLocationUpdate) {
            this.dispatchNotificationEvent('individual_notification', {
              id: notification.id,
              notification_type: notificationType,
              title: notification.title,
              message: notification.message,
              data: notification.data || {},
              is_read: notification.is_read || false,
              created_at: notification.created_at || new Date().toISOString(),
              read_at: notification.read_at
            });
          }

          // Also dispatch specific events based on notification type
          switch (notificationType) {
            case 'driver_location_update':
              this.dispatchNotificationEvent('driver_location_update', notification);
              break;
            case 'document_expiration':
              this.dispatchNotificationEvent('document_expiration', notification);
              break;
            case 'driver_status_change':
              this.dispatchNotificationEvent('driver_status_change', notification);
              break;
            case 'transport_update':
              this.dispatchNotificationEvent('transport_update', notification);
              break;
            case 'system_alert':
              this.dispatchNotificationEvent('system_alert', notification);
              break;
            default:
              this.dispatchNotificationEvent('notification_received', notification);
          }
        } else if (typeof data.message === 'string') {
          this.showNotification(
            getBrowserNotificationTitle('system_message'),
            data.message,
            { sourcePayload: data },
          );
          this.dispatchNotificationEvent('system_message', data);
        } else {
          console.log('Unknown notification message format');
        }
        break;

      case 'driver_location_update':
        console.log('📍 Driver location update received');
        this.dispatchNotificationEvent('driver_location_update', data);
        break;

      case 'driver_status_change':
        console.log('Driver status change notification received');
        if (data.message) {
          this.showNotification(
            getBrowserNotificationTitle('driver_status_change'),
            data.message,
            { sourcePayload: data },
          );
          this.dispatchNotificationEvent('driver_status_change', data);
        }
        break;

      case 'system_message':
        console.log('System notification message received');
        this.showNotification(
          getBrowserNotificationTitle('system_message'),
          data.message,
          { sourcePayload: data },
        );
        this.dispatchNotificationEvent('system_message', data);
        break;

      case 'daily_task_executed':
        console.log('⏰ Daily task executed notification');
        this.dispatchNotificationEvent('daily_task_executed', data);
        break;

      default:
        console.log('Unknown notification type received:', data.type);
        this.dispatchNotificationEvent('unknown', data);
    }
  },

  /**
   * Show browser notification
   */
  showNotification(title, message, options = {}) {
    const { sourcePayload = null } = options;

    // Final guard to prevent location updates from triggering browser notifications/sound.
    if (this.isDriverLocationUpdate(sourcePayload)) {
      return;
    }

    this.playNotificationSound();

    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body: message,
        icon: '/favicon.ico' // Adjust path as needed
      });
    }
  },

  /**
   * Play a short notification tone (Web Audio API, no external assets)
   */
  playNotificationSound() {
    const now = Date.now();
    // Throttle to avoid overlapping sounds on bursts
    if (now - this.lastSoundTime < 500) return;
    this.lastSoundTime = now;

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      const ctx = new AudioCtx();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, ctx.currentTime); // A5 tone

      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);

      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.45);

      // Close context shortly after to free resources
      setTimeout(() => ctx.close(), 500);
    } catch {
      console.warn('Notification sound failed');
    }
  },

  /**
   * Dispatch custom events for UI components
   */
  dispatchNotificationEvent(type, data) {
    window.dispatchEvent(new CustomEvent('notification', {
      detail: { type, data }
    }));
  },

  /**
   * Attempt to reconnect to personal notification channel
   */
  attemptReconnect(error = null, generation = this.generation) {
    this.cancelReconnect();

    if (!this.shouldReconnect || generation !== this.generation || !authService.isAuthenticated()) {
      return;
    }

    const retryAfterMs = Number.isFinite(error?.retryAfterMs) ? error.retryAfterMs : 0;
    if (retryAfterMs > 0) this.retryBaseDelay = retryAfterMs;
    const baseDelay = this.retryBaseDelay || this.reconnectDelay;
    const exponentialDelay = Math.min(
      baseDelay * Math.pow(2, this.reconnectAttempts),
      Math.max(this.maxReconnectDelay, retryAfterMs)
    );
    const jitter = Math.round(exponentialDelay * 0.25 * Math.random());
    const delay = Math.max(retryAfterMs, exponentialDelay) + jitter;
    this.reconnectAttempts += 1;

    console.log(`Attempting to reconnect to personal notifications in ${delay}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (generation !== this.generation || !this.shouldReconnect || !authService.isAuthenticated()) {
        return;
      }
      this.connectToUserChannel().catch(() => {
        // createConnection schedules the next retry while this generation is active.
      });
    }, delay);
  },

  cancelReconnect() {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  },

  /**
   * Connect to personal notification channel
   */
  connectToNotifications() {
    if ('Notification' in window && Notification.permission === 'default') {
      const permissionRequest = Notification.requestPermission();
      permissionRequest?.catch?.(() => {
        console.warn('Notification permission request failed');
      });
    }

    return this.connectToUserChannel();
  },

  /**
   * Disconnect from personal notification channel
   */
  disconnect() {
    // Invalidate callbacks and async results before closing the old socket.
    this.generation += 1;
    this.shouldReconnect = false;
    this.cancelReconnect();

    if (this.connectionAbortController) {
      this.connectionAbortController.abort();
      this.connectionAbortController = null;
    }
    if (this.connectionCancel) {
      this.connectionCancel();
      this.connectionCancel = null;
    }

    const connection = this.userConnection;
    this.userConnection = null;
    if (connection) {
      connection.close(1000, 'Manual disconnect');
      console.log('Disconnected from personal notification channel');
    }

    this.connectionPromise = null;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    this.retryBaseDelay = null;
    this.userId = null;
    this.sessionToken = null;
  },

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return {
      connected: !!this.userConnection && this.userConnection.readyState === WebSocket.OPEN,
      readyState: this.userConnection ? this.userConnection.readyState : null,
      userId: this.userId,
      reconnectAttempts: this.reconnectAttempts,
      connecting: this.isConnecting,
    };
  },

  /**
   * Initialize notification service (call after login)
   */
  initialize() {
    if (!authService.isAuthenticated()) {
      return Promise.reject(new Error('User must be authenticated before initializing notifications'));
    }

    this.shouldReconnect = true;
    return this.connectToNotifications();
  },

  /**
   * Cleanup (call on logout)
   */
  cleanup() {
    this.disconnect();
  },

  // Notification Management API Methods


  /**
   * Fetch user notifications (paginated only - backend requirement)
   */
  async fetchUserNotifications(options = {}) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const {
        page = 1,
        page_size = 30, // Backend NotificationPagination (max: 150)
        signal,
        search = '',
        include_read = null,
        notification_type = '',
        start_date = '',
        end_date = '',
        ordering = '-created_at',
        ...otherParams
      } = options;

      // Use the new dedicated paginated endpoint
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: page_size.toString(),
        ...otherParams
      });

      // Add search parameter if provided (searches title, message content)
      if (search) {
        params.append('search', search);
      }

      // Add read status filter if provided (backend expects include_read)
      if (include_read !== null) {
        params.append('include_read', include_read.toString());
      }

      // Add notification type filter if provided
      if (notification_type) {
        params.append('notification_type', notification_type);
      }

      // Add date range filters if provided
      if (start_date) {
        params.append('start_date', start_date);
      }

      if (end_date) {
        params.append('end_date', end_date);
      }

      // Add ordering if provided
      if (ordering) {
        params.append('ordering', ordering);
      }

      const response = await fetch(`${BASE_URL}notifications/paginated/?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
          'Accept-Language': getActiveLanguage(),
        },
        signal,
      });

      if (response.ok) {
        const data = await response.json();
        const filteredResults = Array.isArray(data?.results)
          ? data.results.filter((notification) => !this.isDriverLocationUpdate(notification))
          : [];

        return {
          ...data,
          results: filteredResults
        };
      } else {
        const payload = await response.json().catch(() => null);
        const error = new Error(
          payload?.error || payload?.detail || `Failed to fetch notifications: ${response.status}`,
        );
        error.status = response.status;
        error.code = payload?.code || payload?.error_code || null;
        const retryAfter = response.headers?.get?.('Retry-After') || null;
        error.retryAfter = retryAfter;
        error.retryAfterMs = parseRetryAfter(retryAfter);
        throw error;
      }
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.error('Notification REST request failed', {
          status: error?.status || null,
          code: error?.code || null,
        });
      }
      throw error;
    }
  },

  /**
   * Mark notification as read
   */
  async markNotificationAsRead(notificationId) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${BASE_URL}notifications/${notificationId}/read/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Notification marked as read:', notificationId);
        this.dispatchNotificationEvent('notification_marked_read', { id: notificationId });
        return data;
      } else {
        throw new Error(`Failed to mark notification as read: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to mark notification as read');
      throw error;
    }
  },

  /**
   * Dismiss notification (delete)
   */
  async dismissNotification(notificationId) {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${BASE_URL}notifications/${notificationId}/dismiss/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🗑️ Notification dismissed:', notificationId);
        this.dispatchNotificationEvent('notification_dismissed', { id: notificationId });
        return data;
      } else {
        throw new Error(`Failed to dismiss notification: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to dismiss notification');
      throw error;
    }
  },

  /**
   * Mark all notifications as read
   */
  async markAllNotificationsAsRead() {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${BASE_URL}notifications/read-all/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ All notifications marked as read');
        this.dispatchNotificationEvent('all_notifications_marked_read', data);
        return data;
      } else {
        throw new Error(`Failed to mark all notifications as read: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read');
      throw error;
    }
  },

  /**
   * Dismiss all notifications (delete all)
   */
  async dismissAllNotifications() {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${BASE_URL}notifications/dismiss-all/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🗑️ All notifications dismissed');
        this.dispatchNotificationEvent('all_notifications_dismissed', data);
        return data;
      } else {
        throw new Error(`Failed to dismiss all notifications: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to dismiss all notifications');
      throw error;
    }
  }
};
